// Phase 8 — Staff Work Center page.
//
// Replaces the legacy Focus Hub. Tasks are derived in real time from
// /api/work-center/tasks (Missing Data Intelligence engine). Visibility is
// already filtered by the backend based on the viewer's role + work_areas.
//
// Layout:
//   • Header strip with summary counts (open / in-progress / snoozed /
//     ignored / high-priority) — pulled from /api/work-center/summary.
//   • Filter bar: status / work-area / priority / "show ignored".
//   • Task list grouped by priority. Each card supports Fix, Snooze, Ignore,
//     In-progress, and Reset.
//   • Empty-state helper text explains why a staff user sees no tasks
//     (typically: their Work Areas are empty).
//
// Notes:
//   • Admin sees ALL tasks. Staff see ONLY tasks whose work_areas overlap
//     with theirs (enforced server-side).
//   • Player/Parent are blocked at the route layer (403 from API anyway).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  AlertTriangle, CheckCircle2, Clock, EyeOff, Loader2,
  PauseCircle, PlayCircle, RefreshCw, RotateCcw, Wrench,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_ORDER = ['critical', 'high', 'medium', 'low'];

const PRIORITY_LABEL = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low',
};

const PRIORITY_BADGE = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-300',
};

const STATUS_BADGE = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  in_progress: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  snoozed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  ignored: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
};

const STATUS_LABEL = {
  open: 'Open', in_progress: 'In Progress', snoozed: 'Snoozed', ignored: 'Ignored',
};

const StatCard = ({ label, value, tone = 'default', testId }) => {
  const toneCls = {
    default: 'border-gray-200 dark:border-gray-700',
    danger: 'border-red-300 dark:border-red-700 bg-red-50/40 dark:bg-red-900/10',
    warn: 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10',
    info: 'border-blue-300 dark:border-blue-700 bg-blue-50/40 dark:bg-blue-900/10',
    muted: 'border-zinc-200 dark:border-zinc-700 bg-zinc-50/40 dark:bg-zinc-900/20',
  }[tone];
  return (
    <Card className={`${toneCls}`}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-testid={testId}>
          {value ?? '—'}
        </div>
      </CardContent>
    </Card>
  );
};

const TaskCard = ({ task, onSnooze, onIgnore, onInProgress, onReset, onFix }) => {
  const statusCls = STATUS_BADGE[task.status] || STATUS_BADGE.open;
  const priorityCls = PRIORITY_BADGE[task.priority] || PRIORITY_BADGE.low;
  const isOverridden = ['snoozed', 'ignored', 'in_progress'].includes(task.status);

  return (
    <Card
      className="border-l-4 hover:shadow-md transition-shadow"
      style={{ borderLeftColor: task.priority === 'critical' ? '#dc2626'
                                : task.priority === 'high' ? '#ea580c'
                                : task.priority === 'medium' ? '#d97706' : '#94a3b8' }}
      data-testid={`task-card-${task.task_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="outline" className={priorityCls} data-testid={`task-priority-${task.task_id}`}>
                {PRIORITY_LABEL[task.priority] || task.priority}
              </Badge>
              <Badge className={statusCls} data-testid={`task-status-${task.task_id}`}>
                {STATUS_LABEL[task.status] || task.status}
              </Badge>
              {(task.work_areas || []).map(w => (
                <Badge key={w} variant="outline" className="text-[10px] border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                  {w}
                </Badge>
              ))}
            </div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate" data-testid={`task-title-${task.task_id}`}>
              {task.title}
            </h3>
            {task.athlete_name && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Athlete: <span className="font-medium">{task.athlete_name}</span>
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5">{task.description}</p>
            {task.snoozed_until && task.status === 'snoozed' && (
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                Snoozed until {new Date(task.snoozed_until).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          {task.action_url && (
            <Button
              size="sm"
              variant="default"
              onClick={() => onFix(task)}
              data-testid={`task-fix-${task.task_id}`}
              className="bg-pfap-green hover:bg-pfap-green/90"
            >
              <Wrench size={14} className="mr-1" />
              Fix
            </Button>
          )}
          {task.status !== 'in_progress' && (
            <Button
              size="sm" variant="outline"
              onClick={() => onInProgress(task)}
              data-testid={`task-in-progress-${task.task_id}`}
            >
              <PlayCircle size={14} className="mr-1" />
              In Progress
            </Button>
          )}
          {task.status !== 'snoozed' && (
            <Button
              size="sm" variant="outline"
              onClick={() => onSnooze(task)}
              data-testid={`task-snooze-${task.task_id}`}
            >
              <PauseCircle size={14} className="mr-1" />
              Snooze
            </Button>
          )}
          {task.status !== 'ignored' && (
            <Button
              size="sm" variant="outline"
              onClick={() => onIgnore(task)}
              data-testid={`task-ignore-${task.task_id}`}
            >
              <EyeOff size={14} className="mr-1" />
              Ignore
            </Button>
          )}
          {isOverridden && (
            <Button
              size="sm" variant="ghost"
              onClick={() => onReset(task)}
              data-testid={`task-reset-${task.task_id}`}
            >
              <RotateCcw size={14} className="mr-1" />
              Reset
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const StaffWorkCenter = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [workAreas, setWorkAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('open');
  const [workAreaFilter, setWorkAreaFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      if (workAreaFilter && workAreaFilter !== 'all') params.work_area = workAreaFilter;
      if (priorityFilter && priorityFilter !== 'all') params.priority = priorityFilter;

      const [tasksRes, summaryRes, catalogRes] = await Promise.all([
        axios.get(`${API}/work-center/tasks`, { params }),
        axios.get(`${API}/work-center/summary`),
        axios.get(`${API}/work-center/work-areas`),
      ]);
      setTasks(tasksRes.data?.tasks || []);
      setViewer(tasksRes.data?.viewer || null);
      setSummary(summaryRes.data || null);
      setWorkAreas(catalogRes.data?.work_areas || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('You do not have access to the Work Center.');
      } else {
        setError('Failed to load Work Center. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, workAreaFilter, priorityFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grouped = useMemo(() => {
    const out = { critical: [], high: [], medium: [], low: [] };
    for (const t of tasks) {
      const p = out[t.priority] ? t.priority : 'low';
      out[p].push(t);
    }
    return out;
  }, [tasks]);

  const handleFix = (task) => {
    if (task.action_url) navigate(task.action_url);
  };

  const handleSnooze = async (task) => {
    // Default: snooze for 7 days.
    const until = new Date();
    until.setDate(until.getDate() + 7);
    try {
      await axios.post(`${API}/work-center/tasks/${task.task_id}/snooze`, {
        snoozed_until: until.toISOString(),
      });
      toast.success('Task snoozed for 7 days');
      fetchData();
    } catch {
      toast.error('Failed to snooze task');
    }
  };

  const handleIgnore = async (task) => {
    const reason = window.prompt('Reason for ignoring (optional):', '') || null;
    try {
      await axios.post(`${API}/work-center/tasks/${task.task_id}/ignore`, { reason });
      toast.success('Task ignored');
      fetchData();
    } catch {
      toast.error('Failed to ignore task');
    }
  };

  const handleInProgress = async (task) => {
    try {
      await axios.post(`${API}/work-center/tasks/${task.task_id}/in-progress`);
      toast.success('Marked as in-progress');
      fetchData();
    } catch {
      toast.error('Failed to mark in-progress');
    }
  };

  const handleReset = async (task) => {
    try {
      await axios.post(`${API}/work-center/tasks/${task.task_id}/reset`);
      toast.success('Task reset');
      fetchData();
    } catch {
      toast.error('Failed to reset task');
    }
  };

  const role = viewer?.role;
  const myAreas = viewer?.work_areas || [];

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="staff-work-center">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Staff Work Center
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
              Live action board derived from the Missing Data Intelligence engine.
              {role === 'Staff' && (
                <> You see tasks for your assigned Work Areas:&nbsp;
                  {myAreas.length === 0 ? (
                    <span className="text-amber-600 dark:text-amber-300 font-medium">
                      none assigned yet — ask an Admin to assign Work Areas to your account.
                    </span>
                  ) : (
                    <span className="font-medium">{myAreas.join(', ')}</span>
                  )}
                </>
              )}
              {role === 'Admin' && <> You see <span className="font-medium">all</span> tasks across the academy.</>}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="work-center-refresh"
          >
            {loading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <RefreshCw size={16} className="mr-1" />}
            Refresh
          </Button>
        </div>

        {/* Summary strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <StatCard label="Open" value={summary.open} tone="info" testId="summary-open" />
            <StatCard label="In Progress" value={summary.in_progress} tone="default" testId="summary-in-progress" />
            <StatCard label="High Priority" value={summary.high_priority} tone="danger" testId="summary-high-priority" />
            <StatCard label="Snoozed" value={summary.snoozed} tone="warn" testId="summary-snoozed" />
            <StatCard label="Ignored" value={summary.ignored} tone="muted" testId="summary-ignored" />
          </div>
        )}

        {/* Filter bar */}
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="min-w-[160px]">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="snoozed">Snoozed</SelectItem>
                  <SelectItem value="ignored">Ignored</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Work Area</label>
              <Select value={workAreaFilter} onValueChange={setWorkAreaFilter}>
                <SelectTrigger data-testid="filter-work-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Work Areas</SelectItem>
                  {workAreas.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Priority</label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger data-testid="filter-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 ml-auto" data-testid="task-count">
              {tasks.length} task{tasks.length === 1 ? '' : 's'}
            </div>
          </CardContent>
        </Card>

        {/* Body */}
        {error && (
          <Card className="border-red-300">
            <CardContent className="p-6 text-center text-red-700 dark:text-red-300" data-testid="work-center-error">
              <AlertTriangle className="mx-auto mb-2" />
              {error}
            </CardContent>
          </Card>
        )}

        {!error && loading && (
          <div className="flex items-center justify-center py-16" data-testid="work-center-loading">
            <Loader2 className="animate-spin text-pfap-green" size={28} />
          </div>
        )}

        {!error && !loading && tasks.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center" data-testid="work-center-empty">
              <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={36} />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                No matching tasks
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                {role === 'Staff' && myAreas.length === 0
                  ? 'Your account has no Work Areas assigned, so no tasks are visible. Ask an Admin to assign Work Areas to you.'
                  : 'Either nothing is missing or your filters are excluding everything. Try clearing the filters.'}
              </p>
            </CardContent>
          </Card>
        )}

        {!error && !loading && tasks.length > 0 && (
          <div className="space-y-6">
            {PRIORITY_ORDER.map(p => grouped[p].length > 0 && (
              <section key={p} data-testid={`section-${p}`}>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                  <Clock size={14} />
                  {PRIORITY_LABEL[p]} <span className="text-gray-400">({grouped[p].length})</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {grouped[p].map(t => (
                    <TaskCard
                      key={t.task_id}
                      task={t}
                      onFix={handleFix}
                      onSnooze={handleSnooze}
                      onIgnore={handleIgnore}
                      onInProgress={handleInProgress}
                      onReset={handleReset}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StaffWorkCenter;
